// src/components/DancerAllocation.js - Event/Dancer Allocation Management
import React, { useState, useEffect } from 'react';
import { useCompetition } from '../competition/CompetitionContext';
import { normalizeDancer, normalizeEvent, formatDancerNames } from '../utils/fieldCompatibility';
import './DancerAllocation.css';

const _host = 'chappie-demo.novosense.africa';
const _company = 'wdsf';

const DancerAllocation = () => {
  const { selectedCompetition } = useCompetition();
  const [dancers, setDancers] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Search and filter states
  const [dancerSearch, setDancerSearch] = useState('');
  const [eventSearch, setEventSearch] = useState('');
  const [selectedDancers, setSelectedDancers] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);

  // Loading states for operations
  const [isAllocating, setIsAllocating] = useState(false);
  const [isDeallocating, setIsDeallocating] = useState(null); // Track which dancer is being deallocated

  // Use the imported formatDancerNames from fieldCompatibility.js
  const formatDancerName = formatDancerNames;

  // Fetch dancers (filtered by selected competition)
  const fetchDancers = async () => {
    try {
      const response = await fetch(`https://${_host}:6003/api/${_company}/couples`);
      if (response.ok) {
        const data = await response.json();
        // Filter dancers by selected competition if available
        const filteredDancers = selectedCompetition
          ? data.filter(dancer => (dancer.competitionId || dancer.competition_id) === selectedCompetition.id)
          : data;
        // Normalize dancers to handle both camelCase and snake_case field names
        const normalizedDancers = filteredDancers.map(dancer => normalizeDancer(dancer));
        setDancers(Array.isArray(normalizedDancers) ? normalizedDancers : []);
      }
    } catch (err) {
      console.error('Error fetching dancers:', err);
      setError('Failed to load dancers');
    }
  };

  // Fetch events (filtered by selected competition)
  const fetchEvents = async () => {
    try {
      const response = await fetch(`https://${_host}:6003/api/${_company}/events`);
      if (response.ok) {
        const data = await response.json();
        // Filter events by selected competition if available
        const filteredEvents = selectedCompetition
          ? data.filter(event => (event.competitionId || event.competition_id) === selectedCompetition.id)
          : data;
        // Normalize events to handle both camelCase and snake_case field names
        const normalizedEvents = filteredEvents.map(event => normalizeEvent(event));
        setEvents(Array.isArray(normalizedEvents) ? normalizedEvents : []);
      }
    } catch (err) {
      console.error('Error fetching events:', err);
      setError('Failed to load events');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDancers();
    fetchEvents();

    // Poll for updates
    const interval = setInterval(() => {
      fetchDancers();
      fetchEvents();
    }, 5000);

    return () => clearInterval(interval);
  }, [selectedCompetition]); // Re-fetch when selected competition changes

  // Filter dancers by search term
  const filteredDancers = dancers.filter(dancer => {
    const searchLower = dancerSearch.toLowerCase();
    const name = formatDancerName(dancer).toLowerCase();
    const clothNumber = dancer.clothNumber?.toString() || '';

    return name.includes(searchLower) || clothNumber.includes(searchLower);
  });

  // Filter events by search term
  const filteredEvents = events.filter(event => {
    const searchLower = eventSearch.toLowerCase();
    const name = event.name?.toLowerCase() || '';
    const itemNumber = event.itemNumber?.toString() || '';

    return name.includes(searchLower) || itemNumber.includes(searchLower);
  }).sort((a, b) => (a.itemNumber || 999) - (b.itemNumber || 999));

  // Get dancers allocated to an event
  const getDancersInEvent = (eventId) => {
    const event = events.find(e => e.id === eventId);
    return event?.dancers || [];
  };

  // Check if dancer is already in selected event
  const isDancerInSelectedEvent = (dancerId) => {
    if (!selectedEvent) return false;
    const eventDancers = getDancersInEvent(selectedEvent.id);
    return eventDancers.some(d => d.coupleId === dancerId);
  };

  // Handle dancer selection
  const toggleDancerSelection = (dancerId) => {
    setSelectedDancers(prev => {
      if (prev.includes(dancerId)) {
        return prev.filter(id => id !== dancerId);
      } else {
        return [...prev, dancerId];
      }
    });
  };

  // Allocate selected dancers to the selected event
  const handleAllocate = async () => {
    if (!selectedEvent || selectedDancers.length === 0) {
      setError('Please select an event and at least one dancer');
      setTimeout(() => setError(null), 3000);
      return;
    }

    try {
      setIsAllocating(true);
      setSuccess(null);
      setError(null);
      const currentEventDancers = getDancersInEvent(selectedEvent.id);

      // Prepare new dancers to add
      const newDancers = selectedDancers
        .filter(dancerId => !isDancerInSelectedEvent(dancerId))
        .map(dancerId => {
          const couple = dancers.find(d => d.id === dancerId);
          return {
            clothNumber: couple.clothNumber,
            name: formatDancerName(couple),
            country: couple.country,
            coupleId: couple.id,
            dancerType: couple.dancerType || 'couple'
          };
        });

      const updatedDancers = [...currentEventDancers, ...newDancers];

      const response = await fetch(`https://${_host}:6003/api/${_company}/events/${selectedEvent.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ dancers: updatedDancers })
      });

      if (!response.ok) {
        throw new Error('Failed to allocate dancers');
      }

      setSuccess(`${newDancers.length} dancer(s) allocated to ${selectedEvent.name}`);
      setTimeout(() => setSuccess(null), 3000);
      setSelectedDancers([]);
      fetchEvents();
    } catch (err) {
      console.error('Error allocating dancers:', err);
      setError('Failed to allocate dancers. Please try again.');
      setTimeout(() => setError(null), 3000);
    } finally {
      setIsAllocating(false);
    }
  };

  // Remove a dancer from an event
  const handleRemoveDancer = async (eventId, dancerId) => {
    try {
      setIsDeallocating(dancerId);
      setError(null);
      setSuccess(null);
      const event = events.find(e => e.id === eventId);

      // Handle dancer ID comparison with both field naming conventions
      const updatedDancers = event.dancers.filter(d => {
        const dancerCoupleId = d.coupleId || d.couple_id;
        return dancerCoupleId !== dancerId;
      });

      const response = await fetch(`https://${_host}:6003/api/${_company}/events/${eventId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ dancers: updatedDancers })
      });

      if (!response.ok) {
        throw new Error('Failed to remove dancer');
      }

      setSuccess('Dancer removed successfully');
      setTimeout(() => setSuccess(null), 3000);
      fetchEvents();
    } catch (err) {
      console.error('Error removing dancer:', err);
      setError('Failed to remove dancer. Please try again.');
      setTimeout(() => setError(null), 3000);
    } finally {
      setIsDeallocating(null);
    }
  };

  // Move dancer from one event to another
  const handleMoveDancer = async (fromEventId, toEventId, dancerId) => {
    try {
      // Remove from source event
      const fromEvent = events.find(e => e.id === fromEventId);
      const dancer = fromEvent.dancers.find(d => d.coupleId === dancerId);
      const updatedFromDancers = fromEvent.dancers.filter(d => d.coupleId !== dancerId);

      // Add to target event
      const toEvent = events.find(e => e.id === toEventId);
      const updatedToDancers = [...toEvent.dancers, dancer];

      // Update both events
      await Promise.all([
        fetch(`https://${_host}:6003/api/${_company}/events/${fromEventId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ dancers: updatedFromDancers })
        }),
        fetch(`https://${_host}:6003/api/${_company}/events/${toEventId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ dancers: updatedToDancers })
        })
      ]);

      setSuccess('Dancer moved successfully');
      setTimeout(() => setSuccess(null), 3000);
      fetchEvents();
    } catch (err) {
      console.error('Error moving dancer:', err);
      setError('Failed to move dancer. Please try again.');
      setTimeout(() => setError(null), 3000);
    }
  };

  if (loading) {
    return <div className="allocation-loading">Loading allocation data...</div>;
  }

  return (
    <div className="dancer-allocation-container">
      <div className="allocation-header">
        <div className="allocation-header-left">
          <h2>Event/Dancer Allocation</h2>
          <p className="allocation-subtitle">Allocate dancers to events and manage assignments</p>
        </div>
        <button
          className="allocate-btn allocate-btn-header"
          onClick={handleAllocate}
          disabled={selectedDancers.length === 0 || !selectedEvent || isAllocating}
        >
          {isAllocating ? (
            <>
              <span className="allocating-spinner">⏳</span>
              Allocating...
            </>
          ) : (
            `Allocate ${selectedDancers.length > 0 ? `(${selectedDancers.length})` : ''} to Event`
          )}
        </button>
      </div>

      <div className="allocation-header-messages">
        {error && <div className="error-banner">{error}</div>}
        {success && <div className="success-banner">{success}</div>}
      </div>

      <div className="allocation-content">
        {/* Left Panel: Dancer Selection */}
        <div className="allocation-panel dancers-panel">
          <h3>All Dancers</h3>
          <input
            type="text"
            className="search-input"
            placeholder="Search by cloth number or name..."
            value={dancerSearch}
            onChange={(e) => setDancerSearch(e.target.value)}
          />

          <div className="dancer-list">
            {filteredDancers.length === 0 ? (
              <p className="empty-message">
                {dancerSearch
                  ? 'No dancers found matching your search.'
                  : 'No dancers available. <a href="/administration">Go to Administration</a> to add dancers first.'
                }
              </p>
            ) : (
              filteredDancers.map(dancer => (
                <div
                  key={dancer.id}
                  className={`dancer-card ${selectedDancers.includes(dancer.id) ? 'selected' : ''} ${
                    isDancerInSelectedEvent(dancer.id) ? 'already-allocated' : ''
                  }`}
                  onClick={() => toggleDancerSelection(dancer.id)}
                >
                  <div className="dancer-number">{dancer.clothNumber}</div>
                  <div className="dancer-info">
                    <div className="dancer-name">{formatDancerName(dancer)}</div>
                    <div className="dancer-country">{dancer.country}</div>
                    {isDancerInSelectedEvent(dancer.id) && (
                      <span className="allocated-badge">Already in event</span>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Middle Panel: Event Selection */}
        <div className="allocation-panel events-panel">
          <h3>Select Event</h3>
          <input
            type="text"
            className="search-input"
            placeholder="Search by item number or event name..."
            value={eventSearch}
            onChange={(e) => setEventSearch(e.target.value)}
          />

          <div className="event-list">
            {filteredEvents.length === 0 ? (
              <p className="empty-message">No events found</p>
            ) : (
              filteredEvents.map(event => (
                <div
                  key={event.id}
                  className={`event-card ${selectedEvent?.id === event.id ? 'selected' : ''}`}
                  onClick={() => setSelectedEvent(event)}
                >
                  <div className="event-header">
                    <span className="event-item-number">{event.itemNumber}</span>
                    <span className="event-name">{event.name}</span>
                  </div>
                  <div className="event-details">
                    <span className="event-round">{event.roundType}</span>
                    <span className="event-dancer-count">
                      {event.dancers?.length || 0} dancers
                    </span>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Right Panel: Current Allocations */}
        <div className="allocation-panel allocations-panel">
          <h3>Current Allocations</h3>
          {selectedEvent ? (
            <>
              <div className="selected-event-info">
                <div className="selected-event-name">
                  <span className="selected-item-number">{selectedEvent.itemNumber}</span>
                  {selectedEvent.name}
                </div>
                <div className="selected-event-meta">
                  {selectedEvent.dancers?.length || 0} dancers allocated
                </div>
              </div>

              <div className="allocated-dancers-list">
                {selectedEvent.dancers && selectedEvent.dancers.length > 0 ? (
                  selectedEvent.dancers.map((dancer, index) => (
                    <div key={index} className="allocated-dancer-card">
                      <div className="allocated-dancer-number">{dancer.clothNumber || dancer.cloth_number}</div>
                      <div className="allocated-dancer-info">
                        <div className="allocated-dancer-name">{dancer.name}</div>
                        <div className="allocated-dancer-country">{dancer.country}</div>
                      </div>
                      <div className="allocated-dancer-actions">
                        <button
                          className={`remove-btn ${isDeallocating === (dancer.coupleId || dancer.couple_id) ? 'loading' : ''}`}
                          onClick={() => handleRemoveDancer(selectedEvent.id, dancer.coupleId || dancer.couple_id)}
                          title="Remove from event"
                          disabled={isDeallocating === (dancer.coupleId || dancer.couple_id)}
                        >
                          {isDeallocating === (dancer.coupleId || dancer.couple_id) ? (
                            <span className="removing-spinner">⏳</span>
                          ) : (
                            '×'
                          )}
                        </button>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="empty-message">No dancers allocated to this event</p>
                )}
              </div>
            </>
          ) : (
            <p className="empty-message">Select an event to view allocations</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default DancerAllocation;
